/**
 * "What happened" (ADR-0092): the request page — one status vocabulary shared with Activity
 * (platform_common.outcomes), the request card, what changed, the agent's steps and its lenses
 * (list, drawing, timeline, people), the step inspector and the auditors' proof.
 */
export default {
  en: {
    status: {
      answered: "Answered",
      refused: "Refused by the rules",
      blocked: "Blocked by safety checks",
      rejected: "Rejected by an approver",
      failed: "Failed",
      cancelled: "Stopped",
      replaced: "Replaced by a correction",
      waiting: "Waiting for a person",
      running: "In progress",
    },
    mode: {
      safe_test: "Safe test",
      safe_testHelp: "A test: nothing was really changed in any system, and nobody was asked to approve.",
      test_real: "Test with real actions",
      test_realHelp: "A test in which the builder allowed real changes in connected systems.",
      live: "Live",
      liveHelp: "A real request: changes in connected systems really happened.",
    },
    replaced: { by: "Replaced by a correction — see the new request", of: "This request replaced an earlier one after a correction — see the earlier request" },
    title: {
      asked: "{{person}} asked {{agent}}",
      schedule: "{{agent}} ran on its schedule",
      webhook: "Another system asked {{agent}}",
      automatic: "{{agent}} ran on its own",
    },
    card: {
      label: "The request",
      question: "Asked",
      answer: "Answered",
      sentTo: "Sent to {{where}}",
      noAnswer: "No answer was sent.",
      hidden: "Only the person who asked, builders, agent admins and auditors can see what was asked and answered. Each step is still shown below.",
      unavailable: "What was asked and answered can't be shown right now, because personal data could not be hidden.",
      started: "Started {{when}}",
      took: "took {{duration}}",
      sofar: "running for {{duration}}",
      cost: "Cost: {{cost}}",
      tokens: "{{input}} tokens in, {{output}} tokens out",
      noRecord: "The agent's own record is not available right now: this page shows what the signed records say, without step times or data.",
      problems_one: "1 system step did not work and had no “if it fails” step.",
      problems_other: "{{count}} system steps did not work and had no “if it fails” step.",
    },
    where: {
      web: "the console chat",
      api: "the program that asked (API)",
      mcp: "the AI app that asked (MCP)",
      widget: "the website chat",
      email: "e-mail",
      chat: "the chat app",
      voice: "the voice call",
      webhook: "the system that called (its reply)",
      schedule: "nowhere: it ran on its schedule",
      unknown: "the person who asked",
    },
    changes: {
      label: "What changed",
      changed: "{{what}} — done in {{system}}",
      simulated: "{{what}} — simulated in {{system}}, nothing was sent",
      failed: "{{what}} — did not happen in {{system}}",
      refused: "{{what}} — refused by the rules, nothing happened in {{system}}",
      undone: "Undone afterwards",
    },
    steps: {
      label: "What the agent did",
      before: "Before the agent's first step",
      visit: "time {{n}} of {{count}}",
      parallel: "At the same time as other steps",
      attempts: "Tried {{count}} times",
      failureRoute: "It didn't work, so the agent went to “{{step}}”",
      child: "Another agent did this step",
      openChild: "Open that agent's own request",
      details: "Details",
      empty: "Nothing else was recorded for this step.",
      passages: "Passages it used",
      score: "match {{score}}%",
      approvedWhat: "What was approved",
      comment: "Comment: “{{text}}”",
      subject: {
        match: "What ran is exactly what was approved (checked against the signed approval).",
        differs: "What ran is NOT what was approved.",
        masked: "What ran can't be compared with what was approved, because personal data is hidden.",
      },
      end: "How it ended",
      none: "No steps were recorded.",
    },
    outcome: {
      done: "done",
      failed: "didn't work",
      refused: "refused by the rules",
      blocked: "blocked by safety checks",
      rejected: "rejected",
      simulated: "simulated (safe test)",
      waiting: "waiting",
      unanswered: "no answer",
      asked_person: "asked a person",
      unchecked: "not checked",
      stopped: "stopped",
      rerouted: "went on elsewhere",
      reminded: "reminder sent",
      escalated: "sent on",
    },
    badge: {
      checked: "Safety checked",
      personal_data_hidden: "Personal data hidden",
      check_unavailable: "Safety check unavailable",
      blocked: "Blocked by safety checks",
    },
    type: {
      llm: "AI step",
      tool: "System step",
      router: "Choice",
      human_approval: "Approval",
      human_input: "Question to a person",
      parallel: "Several at once",
      join: "Wait for all",
      subgraph: "Another agent",
      retrieval: "Knowledge search",
      python: "Code step",
      decision: "Decision",
      step: "Step",
    },
    views: { label: "Show as", list: "List", flow: "Drawing", timeline: "Timeline", people: "People" },
    flow: {
      help: "The agent's drawing. The path this request took is highlighted; steps it did not use are faded.",
      visits: "used {{count}}×",
      attempts: "tried {{count}}×",
      notUsed: "not used",
      error: "if it fails",
      limit: "after the limit",
      unavailable: "The agent's drawing is not available for this version.",
      aria: "{{name}}: {{state}}",
    },
    timeline: {
      help: "How long each step took, from the start of the request.",
      at: "at {{offset}}",
      none: "No step times were recorded for this request.",
    },
    people: {
      help: "The people and agents in this request, and what each did.",
      asked: "Asked the question",
      approved: "Approved",
      rejected: "Rejected",
      tried: "Tried to decide, but it was refused",
      chose: "Chose for the agent",
      agent: "Did the work",
      none: "Nobody else took part.",
    },
    inspector: {
      title: "Step details",
      explanation: "Explanation",
      data: "Data",
      proof: "Rules and proof",
      dataNone: "No data was kept for this step. Only test runs and requests in the Development and Testing stages keep what each step received and made.",
      dataHidden: "Only the person who asked, builders, agent admins and auditors can see the data.",
      sent: "What it received",
      made: "What it made",
      masked: "personal data hidden",
      truncated: "shortened",
      records: "Signed records of this step",
      record: "Record {{seq}}",
      rule: "Rules version {{version}}",
      reasons: "Why: {{reasons}}",
      noRecords: "No signed records are tied to this step.",
      close: "Close",
    },
    proof: {
      open: "Proof",
      title: "Proof (for auditors)",
      views: "Who looked at this request",
      noViews: "Nobody else has looked at this request yet.",
      viewKind: {
        story_access: "read the steps",
        what_happened_viewed: "opened this page",
        step_data_viewed: "looked at the step data",
        content_evidence_viewed: "read the stored content",
        explainability_access: "read the decision trail",
        explain_ask: "asked why",
      },
      download: "Download the evidence pack",
      downloaded: "Evidence pack downloaded.",
      payload: "Record contents",
    },
    builder: { open: "Open in the Builder with this run" },
    ask: {
      retry: "Why was it tried again?",
      refused: "Why was it refused?",
      blocked: "Why was it blocked?",
      approved: "Who approved this, and what did they say?",
      changed: "What did it change?",
      slow: "Why did it take so long?",
      failed: "Why did it fail?",
    },
    // Run story (fix wave 2, stream E): where it ran, retries, memory, team members.
    runStory: {
      env: { dev: "Development", test: "Testing", staging: "Final check", prod: "Live" },
      envHelp: "A real request in {{env}}: changes in connected systems really happened.",
      real: "Real request",
      realHelp: "A real request: changes in connected systems really happened.",
      workedOn_ordinal_one: "worked on the {{count}}st try",
      workedOn_ordinal_two: "worked on the {{count}}nd try",
      workedOn_ordinal_few: "worked on the {{count}}rd try",
      workedOn_ordinal_other: "worked on the {{count}}th try",
      tried: "tried {{count}} times",
      workedLater: "It did not work this time. It worked when the agent tried again (time {{n}}).",
      showLater: "Show",
      tries: "The platform had to try this part {{count}} times: {{why}}.",
      memberTries_one: "{{agent}} had to try again once inside this step: {{why}}.",
      memberTries_other: "{{agent}} had to try again {{count}} times inside this step: {{why}}.",
      triesTag: "{{count}} tries",
      modelTime: "The AI model worked for {{duration}} of this step.",
      again: "Back here after “{{step}}”.",
      reason: {
        model_busy: "the AI model was busy",
        model_unavailable: "the AI model did not answer",
        safety_unavailable: "the safety check did not answer",
        service_unavailable: "a connected service did not answer",
        timeout: "it took too long",
        system_retry: "a system call did not work at first",
        error: "something went wrong",
      },
      after: "After the answer",
      aiStep: "AI step",
    },
    back: "Back",
    // ADR-0108: each AI step's data level and the kind of model it allowed.
    dataLevel: {
      online: "It was given {{level}} data, so a fast online model could answer.",
      local: "It was given {{level}} data, so it stayed on the organisation's own model.",
      because: "Because of: {{what}}",
      level: { public: "Public", internal: "Internal", confidential: "Confidential", restricted: "Restricted" },
      reason: {
        question: "the question",
        history: "the earlier conversation",
        attachments: "the attached documents",
        memory: "what it remembers about the person",
        earlier_steps: "the results of earlier steps",
        step: "the result of “{{step}}”",
        value: "a value it reads",
      },
    },
  },
  ar: {
    status: {
      answered: "تمت الإجابة",
      refused: "رفضته القواعد",
      blocked: "حجبته فحوص الأمان",
      rejected: "رفضه أحد المعتمدين",
      failed: "تعذّر",
      cancelled: "أُوقف",
      replaced: "استُبدل بتصحيح",
      waiting: "بانتظار شخص",
      running: "قيد التنفيذ",
    },
    mode: {
      safe_test: "اختبار آمن",
      safe_testHelp: "اختبار: لم يتغيّر شيء فعليًا في أي نظام، ولم يُطلب من أحد الموافقة.",
      test_real: "اختبار بإجراءات فعلية",
      test_realHelp: "اختبار سمح فيه مُنشئ الوكيل بتغييرات فعلية في الأنظمة المتصلة.",
      live: "فعلي",
      liveHelp: "طلب فعلي: التغييرات في الأنظمة المتصلة حدثت فعلًا.",
    },
    replaced: { by: "استُبدل بتصحيح — اعرض الطلب الجديد", of: "حلّ هذا الطلب محل طلب سابق بعد تصحيح — اعرض الطلب السابق" },
    title: {
      asked: "سأل {{person}} الوكيل {{agent}}",
      schedule: "عمل {{agent}} بحسب جدوله",
      webhook: "طلب نظام آخر من {{agent}}",
      automatic: "عمل {{agent}} من تلقاء نفسه",
    },
    card: {
      label: "الطلب",
      question: "السؤال",
      answer: "الإجابة",
      sentTo: "أُرسلت إلى {{where}}",
      noAnswer: "لم تُرسل إجابة.",
      hidden: "لا يرى ما سُئل وما أُجيب به إلا صاحب الطلب ومُنشئو الوكلاء ومسؤولو الوكلاء والمدققون. وتظهر كل خطوة أدناه مع ذلك.",
      unavailable: "تعذّر عرض ما سُئل وما أُجيب به الآن لأنه لم يمكن إخفاء البيانات الشخصية.",
      started: "بدأ {{when}}",
      took: "استغرق {{duration}}",
      sofar: "يعمل منذ {{duration}}",
      cost: "التكلفة: {{cost}}",
      tokens: "{{input}} رمزًا مُدخلًا، {{output}} رمزًا مُخرجًا",
      noRecord: "سجل الوكيل نفسه غير متاح الآن: تعرض هذه الصفحة ما تقوله السجلات الموقّعة، دون أوقات الخطوات أو بياناتها.",
      problems_zero: "لا خطوات نظام تعذّرت.",
      problems_one: "تعذّرت خطوة نظام واحدة ولم تكن لها خطوة «إذا تعذّر».",
      problems_two: "تعذّرت خطوتا نظام ولم تكن لهما خطوة «إذا تعذّر».",
      problems_few: "تعذّرت {{count}} خطوات نظام ولم تكن لها خطوة «إذا تعذّر».",
      problems_many: "تعذّرت {{count}} خطوة نظام ولم تكن لها خطوة «إذا تعذّر».",
      problems_other: "تعذّرت {{count}} خطوة نظام ولم تكن لها خطوة «إذا تعذّر».",
    },
    where: {
      web: "محادثة وحدة التحكم",
      api: "البرنامج الذي طلب (واجهة البرمجة)",
      mcp: "تطبيق الذكاء الاصطناعي الذي طلب (MCP)",
      widget: "محادثة الموقع",
      email: "البريد الإلكتروني",
      chat: "تطبيق الدردشة",
      voice: "المكالمة الصوتية",
      webhook: "النظام الذي طلب (رده)",
      schedule: "لا أحد: عمل بحسب جدوله",
      unknown: "صاحب الطلب",
    },
    changes: {
      label: "ما الذي تغيّر",
      changed: "{{what}} — نُفّذ في {{system}}",
      simulated: "{{what}} — محاكاة في {{system}}، لم يُرسل شيء",
      failed: "{{what}} — لم يحدث في {{system}}",
      refused: "{{what}} — رفضته القواعد، لم يحدث شيء في {{system}}",
      undone: "تم التراجع عنه لاحقًا",
    },
    steps: {
      label: "ما الذي فعله الوكيل",
      before: "قبل أول خطوة للوكيل",
      visit: "المرة {{n}} من {{count}}",
      parallel: "في الوقت نفسه مع خطوات أخرى",
      attempts: "جُرّب {{count}} مرات",
      failureRoute: "لم ينجح، فانتقل الوكيل إلى «{{step}}»",
      child: "نفّذ هذه الخطوة وكيل آخر",
      openChild: "فتح طلب ذلك الوكيل",
      details: "التفاصيل",
      empty: "لم يُسجَّل شيء آخر لهذه الخطوة.",
      passages: "المقاطع التي استخدمها",
      score: "تطابق {{score}}٪",
      approvedWhat: "ما تمت الموافقة عليه",
      comment: "التعليق: «{{text}}»",
      subject: {
        match: "ما نُفّذ هو بالضبط ما تمت الموافقة عليه (تحققنا منه مقابل الموافقة الموقّعة).",
        differs: "ما نُفّذ ليس ما تمت الموافقة عليه.",
        masked: "تتعذّر مقارنة ما نُفّذ بما تمت الموافقة عليه لأن البيانات الشخصية مخفية.",
      },
      end: "كيف انتهى",
      none: "لم تُسجَّل أي خطوات.",
    },
    outcome: {
      done: "تم",
      failed: "لم ينجح",
      refused: "رفضته القواعد",
      blocked: "حجبته فحوص الأمان",
      rejected: "رُفض",
      simulated: "محاكاة (اختبار آمن)",
      waiting: "ينتظر",
      unanswered: "بلا إجابة",
      asked_person: "سأل شخصًا",
      unchecked: "لم يُفحص",
      stopped: "توقف",
      rerouted: "تابع من مكان آخر",
      reminded: "أُرسل تذكير",
      escalated: "أُحيل",
    },
    badge: {
      checked: "فُحص أمنيًا",
      personal_data_hidden: "أُخفيت بيانات شخصية",
      check_unavailable: "فحص الأمان غير متاح",
      blocked: "حجبته فحوص الأمان",
    },
    type: {
      llm: "خطوة ذكاء اصطناعي",
      tool: "خطوة نظام",
      router: "اختيار",
      human_approval: "موافقة",
      human_input: "سؤال لشخص",
      parallel: "عدة خطوات معًا",
      join: "انتظار الجميع",
      subgraph: "وكيل آخر",
      retrieval: "بحث في المعرفة",
      python: "خطوة برمجية",
      decision: "قرار",
      step: "خطوة",
    },
    views: { label: "اعرض بشكل", list: "قائمة", flow: "مخطط", timeline: "خط زمني", people: "الأشخاص" },
    flow: {
      help: "مخطط الوكيل. المسار الذي سلكه هذا الطلب مُبرز، والخطوات التي لم يستخدمها باهتة.",
      visits: "استُخدمت {{count}}×",
      attempts: "جُرّبت {{count}}×",
      notUsed: "لم تُستخدم",
      error: "إذا تعذّر",
      limit: "بعد الحد",
      unavailable: "مخطط الوكيل غير متاح لهذا الإصدار.",
      aria: "{{name}}: {{state}}",
    },
    timeline: {
      help: "المدة التي استغرقتها كل خطوة منذ بدء الطلب.",
      at: "عند {{offset}}",
      none: "لم تُسجَّل أوقات للخطوات في هذا الطلب.",
    },
    people: {
      help: "الأشخاص والوكلاء في هذا الطلب وما فعله كل منهم.",
      asked: "طرح السؤال",
      approved: "وافق",
      rejected: "رفض",
      tried: "حاول أن يقرر لكن ذلك رُفض",
      chose: "اختار نيابة عن الوكيل",
      agent: "أنجز العمل",
      none: "لم يشارك أحد آخر.",
    },
    inspector: {
      title: "تفاصيل الخطوة",
      explanation: "الشرح",
      data: "البيانات",
      proof: "القواعد والإثبات",
      dataNone: "لم تُحفظ بيانات لهذه الخطوة. لا تحفظ ما تلقّته كل خطوة وما أنتجته إلا تشغيلات الاختبار وطلبات مرحلتي التطوير والاختبار.",
      dataHidden: "لا يرى البيانات إلا صاحب الطلب ومُنشئو الوكلاء ومسؤولو الوكلاء والمدققون.",
      sent: "ما تلقّته",
      made: "ما أنتجته",
      masked: "بيانات شخصية مخفية",
      truncated: "مختصر",
      records: "السجلات الموقّعة لهذه الخطوة",
      record: "السجل {{seq}}",
      rule: "إصدار القواعد {{version}}",
      reasons: "السبب: {{reasons}}",
      noRecords: "لا سجلات موقّعة مرتبطة بهذه الخطوة.",
      close: "إغلاق",
    },
    proof: {
      open: "الإثبات",
      title: "الإثبات (للمدققين)",
      views: "من اطّلع على هذا الطلب",
      noViews: "لم يطّلع أحد آخر على هذا الطلب بعد.",
      viewKind: {
        story_access: "قرأ الخطوات",
        what_happened_viewed: "فتح هذه الصفحة",
        step_data_viewed: "اطّلع على بيانات الخطوات",
        content_evidence_viewed: "قرأ المحتوى المحفوظ",
        explainability_access: "قرأ مسار القرارات",
        explain_ask: "سأل لماذا",
      },
      download: "تنزيل حزمة الأدلة",
      downloaded: "نُزّلت حزمة الأدلة.",
      payload: "محتوى السجل",
    },
    builder: { open: "فتح في أداة البناء مع هذا التشغيل" },
    ask: {
      retry: "لماذا أُعيدت المحاولة؟",
      refused: "لماذا رُفض؟",
      blocked: "لماذا حُجب؟",
      approved: "من وافق على هذا، وماذا قال؟",
      changed: "ما الذي غيّره؟",
      slow: "لماذا استغرق وقتًا طويلًا؟",
      failed: "لماذا تعذّر؟",
    },
    // Run story (fix wave 2, stream E): where it ran, retries, memory, team members.
    runStory: {
      env: { dev: "التطوير", test: "الاختبار", staging: "الفحص النهائي", prod: "التشغيل الفعلي" },
      envHelp: "طلب حقيقي في {{env}}: التغييرات في الأنظمة المتصلة حدثت فعلًا.",
      real: "طلب حقيقي",
      realHelp: "طلب حقيقي: التغييرات في الأنظمة المتصلة حدثت فعلًا.",
      workedOn_ordinal_other: "نجح في المحاولة رقم {{count}}",
      tried: "عدد المحاولات: {{count}}",
      workedLater: "لم ينجح هذه المرة. نجح عندما أعاد الوكيل المحاولة (المرة {{n}}).",
      showLater: "عرض",
      tries: "اضطرت المنصة إلى محاولة هذا الجزء {{count}} مرات: {{why}}.",
      memberTries_zero: "أعاد {{agent}} المحاولة داخل هذه الخطوة: {{why}}.",
      memberTries_one: "أعاد {{agent}} المحاولة مرة واحدة داخل هذه الخطوة: {{why}}.",
      memberTries_two: "أعاد {{agent}} المحاولة مرتين داخل هذه الخطوة: {{why}}.",
      memberTries_few: "أعاد {{agent}} المحاولة {{count}} مرات داخل هذه الخطوة: {{why}}.",
      memberTries_many: "أعاد {{agent}} المحاولة {{count}} مرة داخل هذه الخطوة: {{why}}.",
      memberTries_other: "أعاد {{agent}} المحاولة {{count}} مرة داخل هذه الخطوة: {{why}}.",
      triesTag: "{{count}} محاولات",
      modelTime: "عمل نموذج الذكاء الاصطناعي {{duration}} من وقت هذه الخطوة.",
      again: "عاد إلى هنا بعد «{{step}}».",
      reason: {
        model_busy: "كان نموذج الذكاء الاصطناعي مشغولًا",
        model_unavailable: "لم يستجب نموذج الذكاء الاصطناعي",
        safety_unavailable: "لم يستجب فحص السلامة",
        service_unavailable: "لم تستجب خدمة متصلة",
        timeout: "استغرق وقتًا طويلًا",
        system_retry: "لم ينجح استدعاء نظام في البداية",
        error: "حدث خطأ",
      },
      after: "بعد الإجابة",
      aiStep: "خطوة ذكاء اصطناعي",
    },
    back: "رجوع",
    dataLevel: {
      online: "أُعطيت بيانات {{level}}، فأمكن أن يجيب نموذج سريع عبر الإنترنت.",
      local: "أُعطيت بيانات {{level}}، فبقيت على نموذج المؤسسة الخاص.",
      because: "بسبب: {{what}}",
      level: { public: "عامة", internal: "داخلية", confidential: "سرية", restricted: "مقيدة" },
      reason: {
        question: "السؤال",
        history: "المحادثة السابقة",
        attachments: "المستندات المرفقة",
        memory: "ما يتذكره عن الشخص",
        earlier_steps: "نتائج الخطوات السابقة",
        step: "نتيجة «{{step}}»",
        value: "قيمة يقرؤها",
      },
    },
  },
};
